// src/components/PortfolioDashboard.js
import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  BarChart3, 
  Eye,
  Download,
  Moon,
  Sun,
  Bell,
  Search,
  Filter,
  Newspaper
} from 'lucide-react';
import StockTable from './portfolio/StockTable';
import PortfolioSummary from './portfolio/PortfolioSummary';
import AnalyticsCharts from './portfolio/AnalyticsCharts';
import NewsFeed from './portfolio/NewsFeed';
import Watchlist from './portfolio/Watchlist';
import { usePortfolio } from '../hooks/usePortfolio';
import AdvancedStockCharts from './portfolio/AdvancedStockCharts';
import PortfolioAnalytics from './portfolio/PortfolioAnalytics';
import { useWebSocket } from '../hooks/useWebSocket';
import { getTradingStatus } from '../utils/tradingSession';
import TradingStatusIndicator from './portfolio/TradingStatusIndicator'
import CandlestickChart from './portfolio/CandlestickChart';

//const { realTimePrices, isConnected } = useWebSocket();

const PortfolioDashboard = () => {
  const [darkMode, setDarkMode] = useState(false);
  const [activeTab, setActiveTab] = useState('portfolio');
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
//  const [portfolio, setPortfolio] = useState([]);

  const { portfolio, refreshPortfolio, fetchPortfolio } = usePortfolio();
  const { realTimePrices, connectWebSocket } = useWebSocket();
  const [tradingStatus, setTradingStatus] = useState({});

  useEffect(() => {
    // Connect to WebSocket for real-time updates
    connectWebSocket(portfolio.map(stock => stock.symbol));
    connectWebSocket((portfolio || []).map(stock => stock.symbol));
    const loadPortfolioData = async () => {
      try {
        setLoading(true);
        console.log("use effect Portfolio data loaded:", portfolio);
        setError(null);
      } catch (err) {
        setError('Failed to load portfolio data');
        console.error('Error fetching portfolio:', err);
      } finally {
        setLoading(false);
      }
    };
    if (portfolio.length === 0) loadPortfolioData();

    // Apply dark mode to entire document
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // Global error handler
    const handleError = (event) => {
        console.error('Global error:', event.error, event);
    };

    // Global unhandled rejection handler
    const handleRejection = (event) => {
        console.error('Unhandled rejection:', event.reason, event);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
        window.removeEventListener('error', handleError);
        window.removeEventListener('unhandledrejection', handleRejection);
    };    
    
  }, [darkMode, portfolio]);

  const handleButtonClick = async (action, event) => {
    try {
      event.preventDefault();
      event.stopPropagation();
      
      switch (action) {
        case 'refresh':
          await refreshPortfolio();
          break;
        case 'export':
          await exportToCSV();
          break;
        case 'toggleDarkMode':
          setDarkMode(prev => !prev);
          break;
        case 'search':
          // Handle search if needed
          break;
        default:
          console.warn('Unknown action:', action);
      }
    } catch (error) {
      console.error('Error in button click:', error);
    }
  };  

  // Enhanced portfolio data with real-time prices
  const enhancedPortfolio = portfolio.map(stock => ({
    ...stock,
    currentPrice: realTimePrices[stock.symbol] || stock.market_price,
    marketValue: stock.quantity * (realTimePrices[stock.symbol] || stock.market_price),
    unrealizedPnl: stock.quantity * ((realTimePrices[stock.symbol] || stock.market_price) - stock.average_cost)
  }));

  const filteredPortfolio = enhancedPortfolio.filter(stock => 
    stock.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
    stock.name.toLowerCase().includes(searchTerm.toLowerCase())
  ).filter(stock => {
    switch (filter) {
      case 'gainers': return stock.unrealizedPnl > 0;
      case 'losers': return stock.unrealizedPnl < 0;
      case 'oversold': return getRSAData(stock.symbol).status === 'oversold';
      case 'overbought': return getRSAData(stock.symbol).status === 'overbought';
      default: return true;
    }
  });

  const exportToCSV = () => {
    const csvContent = [
      ['Symbol', 'Name', 'Quantity', 'Avg Cost', 'Current Price', 'Market Value', 'P&L'],
      ...filteredPortfolio.map(stock => [
        stock.symbol,
        stock.name,
        stock.quantity,
        stock.average_cost,
        stock.currentPrice,
        stock.marketValue,
        stock.unrealizedPnl
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'portfolio.csv';
    link.click();
  };

  return (
    <div className={`min-h-screen transition-colors duration-200 ${
      darkMode ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-900'
    }`}>
      {/* Header */}
      <header className={`sticky top-0 z-50 ${
        darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      } border-b shadow-sm`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              {/* <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                📊 Duke's Smart Portfolio
              </h1> */}
              <TradingStatusIndicator status={tradingStatus} />
              
              {/* Navigation Tabs */}
              <nav className="flex space-x-1">
                {['portfolio', 'charts', 'analytics', 'news', 'watchlist'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      activeTab === tab
                        ? darkMode
                          ? 'bg-blue-600 text-white'
                          : 'bg-blue-100 text-blue-700'
                        : darkMode
                        ? 'text-gray-300 hover:text-white'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </nav>
            </div>

            <div className="flex items-center space-x-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search stocks..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`pl-10 pr-4 py-2 rounded-lg border ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                />
              </div>

              {/* Dark Mode Toggle */}
              <button
                onClick={() => setDarkMode(!darkMode)}
                className={`p-2 rounded-lg ${
                  darkMode ? 'bg-gray-700 text-yellow-400' : 'bg-gray-200 text-gray-700'
                } hover:opacity-80 transition-opacity`}
              >
                {darkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>

              {/* Export Button */}
              <button
                onClick={(e) => handleButtonClick('export', e)}
                className={`p-2 rounded-lg flex items-center space-x-2 ${
                  darkMode ? 'bg-green-700 text-white' : 'bg-green-600 text-white'
                } hover:opacity-80 transition-opacity`}
              >
                <Download size={16} />
                <span className="text-sm">Export</span>
              </button>

              {/* Refresh Button */}
              <button
                onClick={(e) => handleButtonClick('refresh', e)}
                className={`p-2 rounded-lg flex items-center space-x-2 ${
                  darkMode ? 'bg-blue-700 text-white' : 'bg-blue-600 text-white'
                } hover:opacity-80 transition-opacity`}
                disabled={loading}
              >
                <Eye size={16} />
                <span className="text-sm">{loading ? 'Refreshing...' : 'Refresh'}</span>
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center space-x-4 pb-4">
            <Filter size={16} className={darkMode ? 'text-gray-400' : 'text-gray-600'} />
            {['all', 'gainers', 'losers', 'oversold', 'overbought'].map(filterType => (
              <button
                key={filterType}
                onClick={() => setFilter(filterType)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  filter === filterType
                    ? darkMode
                      ? 'bg-blue-600 text-white'
                      : 'bg-blue-100 text-blue-700'
                    : darkMode
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {filterType.charAt(0).toUpperCase() + filterType.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Tab Content */}
        {activeTab === 'portfolio' && (
            <>
                <PortfolioSummary portfolio={enhancedPortfolio} darkMode={darkMode} />
                <StockTable 
                    portfolio={filteredPortfolio} 
                    darkMode={darkMode} 
                    onRefresh={refreshPortfolio}
                    loading={loading}
                    realTimePrices={realTimePrices} // ← Pass realTimePrices here                    
                />
            </>
        )}

        {activeTab === 'charts' && (
        <AdvancedStockCharts 
            symbol={portfolio.map(stock => stock.symbol)} 
            darkMode={darkMode}
            //timeframe={timeframe}
        />
        )}        

        {activeTab === 'analytics' && (
            <PortfolioAnalytics 
                portfolio={enhancedPortfolio}
                darkMode={darkMode}
            />            
          // <AnalyticsCharts 
          //   portfolio={enhancedPortfolio} 
          //   darkMode={darkMode} 
          // />
         
        )}

        {activeTab === 'news' && (
          <NewsFeed 
            symbols={portfolio.map(stock => stock.symbol)}
            darkMode={darkMode}
          />
        )}

        {activeTab === 'watchlist' && (
          <Watchlist 
            darkMode={darkMode}
            portfolio={portfolio}
          />
        )}

        {error && (
          <div className={`mt-4 p-4 rounded-lg ${
            darkMode ? 'bg-red-900 text-red-100' : 'bg-red-100 text-red-700'
          }`}>
            {error}
          </div>
        )}
      </main>
    </div>
  );
};

// Mock RSA function (replace with actual implementation)
const getRSAData = (symbol) => {
  const rsaValues = {
    'AAPL': { value: 65, status: 'overbought' },
    'GOOGL': { value: 35, status: 'oversold' },
    'TSLA': { value: 72, status: 'overbought' },
    // ... other symbols
  };
  return rsaValues[symbol] || { value: 50, status: 'neutral' };
};

export default PortfolioDashboard;